import type {
  CardFieldChangeEvent,
  CardFieldElementType,
  PaypercutElementsAppearance,
} from '../types/elements';
import {
  resolveCardFieldAppearance,
  resolveCardFieldAppearanceRule,
} from './card-field-appearance';
import { CardBrandBadges, type CardBrandBadgeState } from './card-brand-badges';
import { resolveCardBrandLaneGeometry } from './card-brand-geometry';
import type { CardCollectionFieldPresentation } from './card-collection-provider';
import type { CardFieldCopy } from './card-field-copy';

const applyStyles = (target: HTMLElement | SVGElement, styles: Record<string, string>): void => {
  for (const [property, value] of Object.entries(styles)) {
    // Appearance properties are validated against the public camel-case
    // allow-list before reaching this production surface.
    target.style.setProperty(
      property.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`),
      value,
    );
  }
};

const resetStyle = (target: HTMLElement | SVGElement): void => {
  target.removeAttribute('style');
};

const LOADING_PULSE_DURATION_MILLISECONDS = 1_500;

export type CardFieldSurfaceState = Pick<CardFieldChangeEvent, 'empty' | 'error' | 'brand'>;

export type CardFieldSurfaceOptions = {
  type: CardFieldElementType;
  copy: CardFieldCopy;
  appearance?: PaypercutElementsAppearance;
  showIcon?: boolean;
};

type ResolvedLabelLayout = 'above' | 'floating';

const resolveLayouts = (
  appearance: PaypercutElementsAppearance | undefined,
): { input: 'condensed' | 'spaced'; label: ResolvedLabelLayout } => {
  const input = appearance?.inputs ?? 'condensed';
  const requestedLabel = appearance?.labels ?? 'auto';
  return {
    input,
    label:
      requestedLabel === 'auto' ? (input === 'condensed' ? 'floating' : 'above') : requestedLabel,
  };
};

const displayedCardBrand = (brand: string | undefined): 'visa' | 'mastercard' | undefined => {
  const normalized = brand
    ?.trim()
    .toLowerCase()
    .replace(/[ _-]+/g, '');
  if (normalized === 'visa') return 'visa';
  if (normalized === 'mastercard') return 'mastercard';
  return undefined;
};

/**
 * Framework-neutral Paypercut-owned visual shell around one secure provider
 * iframe. The SDK owns labels, badges, focus, and visible validation; the
 * provider owns the sensitive input and supplies only PCI-safe state.
 */
export class CardFieldSurface {
  readonly root: HTMLDivElement;
  readonly providerMount: HTMLDivElement;
  readonly inputFrame: HTMLDivElement;
  readonly labelAbove: HTMLSpanElement;
  readonly floatingLabel: HTMLSpanElement;
  readonly errorMessage: HTMLParagraphElement;
  readonly loadingIndicator: HTMLDivElement;
  readonly loadingLabelLine: HTMLSpanElement;
  readonly loadingValueLine: HTMLSpanElement;
  readonly brandBadges: CardBrandBadges | null;
  readonly brandLane: HTMLDivElement | null;
  private readonly type: CardFieldElementType;
  private readonly copy: CardFieldCopy;
  private readonly showIcon: boolean;
  private appearance: PaypercutElementsAppearance | undefined;
  private focused = false;
  private ready = false;
  private validation: CardFieldSurfaceState = { empty: true };
  private visibleError = false;
  private submissionErrorVisible = false;
  private loadingAnimation: Animation | null = null;
  private appearanceTransition = false;
  private destroyed = false;

  constructor(container: HTMLElement, options: CardFieldSurfaceOptions) {
    this.type = options.type;
    this.copy = options.copy;
    this.appearance = options.appearance;
    this.showIcon = this.type === 'cardNumber' && (options.showIcon ?? true);

    this.root = document.createElement('div');
    this.root.dataset.paypercutCardField = '';
    this.root.dataset.field = this.type;
    this.root.lang = this.copy.locale;

    this.labelAbove = document.createElement('span');
    this.labelAbove.dataset.paypercutCardFieldLabel = 'above';
    this.labelAbove.textContent = this.copy.ariaLabel;

    this.inputFrame = document.createElement('div');
    this.inputFrame.dataset.paypercutCardFieldInput = '';
    this.inputFrame.dataset.field = this.type;

    this.floatingLabel = document.createElement('span');
    this.floatingLabel.dataset.paypercutCardFieldLabel = 'floating';
    this.floatingLabel.setAttribute('aria-hidden', 'true');
    this.floatingLabel.textContent = this.copy.ariaLabel;

    this.providerMount = document.createElement('div');
    this.providerMount.dataset.paypercutCardFieldProvider = '';

    this.loadingIndicator = document.createElement('div');
    this.loadingIndicator.dataset.paypercutCardFieldLoading = '';
    this.loadingIndicator.setAttribute('aria-hidden', 'true');
    this.loadingLabelLine = document.createElement('span');
    this.loadingLabelLine.dataset.paypercutCardFieldLoadingLine = 'label';
    this.loadingValueLine = document.createElement('span');
    this.loadingValueLine.dataset.paypercutCardFieldLoadingLine = 'value';
    this.loadingIndicator.append(this.loadingLabelLine, this.loadingValueLine);

    this.brandBadges =
      this.type === 'cardNumber'
        ? new CardBrandBadges({
            accepted: `${this.copy.ariaLabel}: Visa, Mastercard`,
            invalid: this.copy.errors.invalid,
            visa: 'Visa',
            mastercard: 'Mastercard',
          })
        : null;
    this.brandLane = this.type === 'cardNumber' ? document.createElement('div') : null;
    if (this.brandLane) this.brandLane.dataset.paypercutCardBrandLane = '';

    this.errorMessage = document.createElement('p');
    this.errorMessage.dataset.paypercutCardFieldError = '';
    this.errorMessage.setAttribute('role', 'alert');
    this.errorMessage.setAttribute('aria-live', 'polite');

    this.inputFrame.appendChild(this.floatingLabel);
    this.inputFrame.appendChild(this.providerMount);
    if (this.brandBadges && this.brandLane) {
      this.brandLane.appendChild(this.brandBadges.root);
      this.inputFrame.appendChild(this.brandLane);
    }
    this.inputFrame.appendChild(this.loadingIndicator);
    this.root.appendChild(this.labelAbove);
    this.root.appendChild(this.inputFrame);
    this.root.appendChild(this.errorMessage);
    container.appendChild(this.root);
    this.render();
  }

  getPresentation(): CardCollectionFieldPresentation {
    const layouts = resolveLayouts(this.appearance);
    const floatingLabelActive = this.focused || !this.validation.empty;
    return {
      floatingLabels: layouts.label === 'floating',
      floatingLabelActive,
      placeholder:
        layouts.label === 'floating' && !floatingLabelActive ? '' : this.copy.placeholder,
    };
  }

  updateAppearance(appearance: PaypercutElementsAppearance | undefined): void {
    this.assertAlive();
    this.appearance = appearance;
    this.render();
  }

  beginAppearanceTransition(): void {
    this.assertAlive();
    this.appearanceTransition = true;
    this.render();
  }

  handleReady(): void {
    this.assertAlive();
    if (this.ready && !this.appearanceTransition) return;
    this.ready = true;
    this.appearanceTransition = false;
    this.render();
  }

  handleChange(change: CardFieldSurfaceState): void {
    this.assertAlive();
    this.validation = {
      empty: change.empty,
      ...(change.brand ? { brand: change.brand } : {}),
      ...(change.error ? { error: { ...change.error } } : {}),
    };
    if (!change.error) this.submissionErrorVisible = false;
    this.visibleError =
      Boolean(change.error) && (this.submissionErrorVisible || (!this.focused && !change.empty));
    this.render();
  }

  handleFocus(): void {
    this.assertAlive();
    this.focused = true;
    this.visibleError = this.submissionErrorVisible && Boolean(this.validation.error);
    this.render();
  }

  handleBlur(): void {
    this.assertAlive();
    this.focused = false;
    this.visibleError =
      Boolean(this.validation.error) && (this.submissionErrorVisible || !this.validation.empty);
    this.render();
  }

  revealValidationError(errorType: 'incomplete' | 'invalid'): void {
    this.assertAlive();
    if (!this.validation.error) {
      this.validation = {
        ...this.validation,
        error: { type: errorType, message: this.copy.errors[errorType] },
      };
    }
    this.submissionErrorVisible = Boolean(this.validation.error);
    this.visibleError = Boolean(this.validation.error);
    this.render();
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.loadingAnimation?.cancel();
    this.loadingAnimation = null;
    this.root.remove();
  }

  private render(): void {
    const resolved = resolveCardFieldAppearance(this.appearance);
    const layouts = resolveLayouts(this.appearance);
    const presentation = this.getPresentation();
    const invalid = this.visibleError;
    const errorText = invalid ? (this.validation.error?.message ?? '') : '';
    const brandState: CardBrandBadgeState = invalid
      ? 'invalid'
      : (displayedCardBrand(this.validation.brand) ?? 'accepted');
    const brandLane = resolveCardBrandLaneGeometry(resolved.variables.spacingUnit, brandState);
    const loading = !this.ready;

    this.root.dataset.focused = this.focused ? 'true' : 'false';
    this.root.dataset.invalid = invalid ? 'true' : 'false';
    this.root.dataset.loading = loading ? 'true' : 'false';
    this.root.dataset.appearanceTransition = this.appearanceTransition ? 'updating' : 'idle';
    this.root.setAttribute('aria-busy', loading || this.appearanceTransition ? 'true' : 'false');
    this.root.dataset.theme = resolved.theme;
    this.root.dataset.inputLayout = layouts.input;
    this.root.dataset.labelLayout = layouts.label;
    this.inputFrame.dataset.focused = this.focused ? 'true' : 'false';
    this.inputFrame.dataset.invalid = invalid ? 'true' : 'false';
    this.inputFrame.inert = this.appearanceTransition;

    resetStyle(this.root);
    applyStyles(this.root, {
      display: 'grid',
      width: '100%',
      minWidth: '0',
      gap: resolved.variables.labelSpacing,
      boxSizing: 'border-box',
      color: resolved.variables.colorText,
      colorScheme: resolved.theme,
      fontFamily: resolved.variables.fontFamily,
      fontSize: resolved.variables.fontSizeBase,
      fontWeight: resolved.variables.fontWeightNormal,
      lineHeight: resolved.variables.lineHeight,
    });

    this.labelAbove.hidden = layouts.label !== 'above';
    resetStyle(this.labelAbove);
    applyStyles(this.labelAbove, {
      display: layouts.label === 'above' ? 'block' : 'none',
      color: resolved.variables.colorText,
      fontFamily: resolved.variables.fontFamily,
      fontSize: '0.875em',
      fontWeight: resolved.variables.fontWeightMedium,
      lineHeight: resolved.variables.lineHeight,
      ...resolveCardFieldAppearanceRule(this.appearance, '.Label'),
    });

    resetStyle(this.inputFrame);
    applyStyles(this.inputFrame, {
      position: 'relative',
      width: '100%',
      minWidth: '0',
      height: layouts.label === 'floating' ? '56px' : '48px',
      overflow: 'hidden',
      boxSizing: 'border-box',
      borderStyle: 'solid',
      borderWidth: '1px',
      borderColor: this.focused
        ? resolved.variables.colorFocus
        : invalid
          ? resolved.variables.colorDanger
          : resolved.variables.colorBorder,
      borderRadius: resolved.variables.borderRadius,
      background: resolved.variables.colorSurface,
      color: resolved.variables.colorText,
      transition: resolved.disableAnimations
        ? 'none'
        : 'border-color 150ms ease, box-shadow 150ms ease',
      boxShadow: this.focused
        ? `inset 0 0 0 1px ${resolved.variables.colorFocus}, ` +
          `0 0 0 3px color-mix(in srgb, ${resolved.variables.colorFocus} 25%, transparent)`
        : invalid
          ? `inset 0 0 0 1px ${resolved.variables.colorDanger}`
          : 'none',
      '--ppc-card-brand-lane-width': this.showIcon ? brandLane.laneWidth : '0px',
      pointerEvents: this.appearanceTransition ? 'none' : 'auto',
      ...resolveCardFieldAppearanceRule(this.appearance, '.Input'),
      ...(invalid ? resolveCardFieldAppearanceRule(this.appearance, '.Input--invalid') : {}),
      ...(this.focused ? resolveCardFieldAppearanceRule(this.appearance, '.Input:focus') : {}),
    });

    this.floatingLabel.hidden = layouts.label !== 'floating';
    this.floatingLabel.dataset.state = presentation.floatingLabelActive ? 'active' : 'resting';
    resetStyle(this.floatingLabel);
    applyStyles(this.floatingLabel, {
      display: layouts.label === 'floating' ? 'block' : 'none',
      position: 'absolute',
      zIndex: '2',
      top: presentation.floatingLabelActive ? resolved.variables.spacingUnit : '50%',
      left: `calc(${resolved.variables.spacingUnit} * 3)`,
      pointerEvents: 'none',
      color: presentation.floatingLabelActive
        ? resolved.variables.colorTextSecondary
        : resolved.variables.colorTextPlaceholder,
      fontFamily: resolved.variables.fontFamily,
      fontSize: presentation.floatingLabelActive ? '0.6875em' : '1em',
      fontWeight: resolved.variables.fontWeightNormal,
      lineHeight: presentation.floatingLabelActive ? '1rem' : resolved.variables.lineHeight,
      transform: presentation.floatingLabelActive ? 'none' : 'translateY(-50%)',
      transformOrigin: 'left center',
      transition: resolved.disableAnimations
        ? 'none'
        : 'top 150ms ease, transform 150ms ease, font-size 150ms ease, line-height 150ms ease, color 150ms ease',
      ...resolveCardFieldAppearanceRule(this.appearance, '.Label'),
    });

    resetStyle(this.providerMount);
    applyStyles(this.providerMount, {
      width: this.showIcon ? 'calc(100% - var(--ppc-card-brand-lane-width))' : '100%',
      minWidth: '0',
      height: '100%',
      overflow: 'hidden',
      boxSizing: 'border-box',
      transition: resolved.disableAnimations ? 'none' : 'width 150ms ease',
    });

    if (this.brandBadges && this.brandLane) {
      this.brandBadges.setState(brandState);
      this.brandBadges.setVisible(this.showIcon);
      resetStyle(this.brandLane);
      applyStyles(this.brandLane, {
        display: this.showIcon ? 'flex' : 'none',
        position: 'absolute',
        zIndex: '3',
        top: '0',
        right: '0',
        width: 'var(--ppc-card-brand-lane-width)',
        height: '100%',
        boxSizing: 'border-box',
        alignItems: 'center',
        justifyContent: 'flex-end',
        paddingRight: brandLane.endInset,
        pointerEvents: 'none',
        transition: resolved.disableAnimations ? 'none' : 'width 150ms ease',
      });
      resetStyle(this.brandBadges.root);
      applyStyles(this.brandBadges.root, {
        display: this.showIcon ? 'flex' : 'none',
        alignItems: 'center',
        gap: brandLane.badgeGap,
        pointerEvents: 'none',
        color: resolved.variables.colorDanger,
      });
    }

    this.loadingIndicator.hidden = !loading;
    resetStyle(this.loadingIndicator);
    applyStyles(this.loadingIndicator, {
      display: loading ? 'flex' : 'none',
      position: 'absolute',
      zIndex: '4',
      inset: '0',
      boxSizing: 'border-box',
      flexDirection: 'column',
      justifyContent: 'center',
      gap: `calc(${resolved.variables.spacingUnit} * 1.5)`,
      padding: `calc(${resolved.variables.spacingUnit} * 3)`,
      overflow: 'hidden',
      background: resolved.variables.colorSurface,
      cursor: 'progress',
    });

    const loadingLineColor =
      `color-mix(in srgb, ${resolved.variables.colorBorder} 72%, ` +
      `${resolved.variables.colorSurface})`;
    resetStyle(this.loadingLabelLine);
    applyStyles(this.loadingLabelLine, {
      display: layouts.label === 'floating' ? 'block' : 'none',
      width: '28%',
      height: '6px',
      borderRadius: resolved.variables.borderRadius,
      background: loadingLineColor,
    });
    resetStyle(this.loadingValueLine);
    applyStyles(this.loadingValueLine, {
      display: 'block',
      width: this.type === 'cardNumber' ? '58%' : this.type === 'cardExpiry' ? '48%' : '42%',
      height: '10px',
      borderRadius: resolved.variables.borderRadius,
      background: loadingLineColor,
    });

    const prefersReducedMotion =
      this.root.ownerDocument?.defaultView?.matchMedia?.('(prefers-reduced-motion: reduce)')
        .matches ?? false;
    const supportsAnimation = typeof this.loadingValueLine.animate === 'function';
    if (loading && !resolved.disableAnimations && !prefersReducedMotion && supportsAnimation) {
      this.loadingAnimation ??= this.loadingValueLine.animate(
        [{ opacity: 0.55 }, { opacity: 1 }, { opacity: 0.55 }],
        {
          duration: LOADING_PULSE_DURATION_MILLISECONDS,
          iterations: Number.POSITIVE_INFINITY,
          easing: 'ease-in-out',
        },
      );
    } else {
      this.loadingAnimation?.cancel();
      this.loadingAnimation = null;
    }

    this.errorMessage.hidden = !invalid;
    this.errorMessage.textContent = errorText;
    resetStyle(this.errorMessage);
    applyStyles(this.errorMessage, {
      display: invalid ? 'block' : 'none',
      margin: '0',
      color: resolved.variables.colorDanger,
      fontFamily: resolved.variables.fontFamily,
      fontSize: '0.875em',
      fontWeight: resolved.variables.fontWeightNormal,
      lineHeight: resolved.variables.lineHeight,
      ...resolveCardFieldAppearanceRule(this.appearance, '.Error'),
    });
  }

  private assertAlive(): void {
    if (this.destroyed) {
      throw new Error('[Paypercut Elements] Cannot update a destroyed card field surface.');
    }
  }
}
